import React from 'react';
import styled from 'styled-components';
import { Theme } from '@routific/hive';
import { useTranslation } from 'react-i18next';
import LocationStatusLabel from './LocationStatusLabel';
import { MAX_OFFLINE_MINUTES, ONE_HOUR_IN_MINUTES, EIGHT_HOURS } from '../../../../constants';

const { Spacing, Colors } = Theme;

interface Props {
  driverName: string;
  lastLocationUpdateMinutes: number | undefined;
}

export const PopoverContentDiv = styled.div<{ $isLive: boolean }>`
  height: 80px;
  width: 336px;
  padding: ${Spacing.mdSpacing};
  & > .driver-name {
    font-weight: 500;
    line-height: ${Spacing.lgSpacing};
    margin-bottom: ${Spacing.smSpacing};
  }

  & > .bottom-content {
    display: flex;
    justify-content: space-between;
  }

  & .location-update-timestamp {
    line-height: ${Spacing.mdSpacing};
    font-size: 12px;
    height: ${Spacing.mdSpacing};
    color: ${({ $isLive }) => ($isLive ? Colors.success6 : Colors.textSecondary)};
  }
`;

export const getTimeStampString = (lastLocationUpdateMinutes: number | undefined, translation: any) => {
  if (lastLocationUpdateMinutes === undefined) return translation('assignDriver.popover.noUpdates');
  if (lastLocationUpdateMinutes <= 1) {
    return translation('assignDriver.popover.updateLessThanTwoMins', { minutes: lastLocationUpdateMinutes });
  }
  if (lastLocationUpdateMinutes <= (ONE_HOUR_IN_MINUTES - 1)) {
    return translation('assignDriver.popover.updateLessThanOneHour', { minutes: lastLocationUpdateMinutes });
  }
  const nearestHour = Math.round(lastLocationUpdateMinutes / ONE_HOUR_IN_MINUTES);
  if (nearestHour <= 1) {
    return translation('assignDriver.popover.updateLessThanTwoHours', { hours: nearestHour });
  }
  if (nearestHour < EIGHT_HOURS) {
    return translation('assignDriver.popover.updateLessThanEightHours', { hours: nearestHour });
  }
  return translation('assignDriver.popover.overEightHours');
};

const DriverLocationPopoverContent: React.FC<Props> = ({ driverName, lastLocationUpdateMinutes }) => {
  const { t } = useTranslation('components');
  const isLive = lastLocationUpdateMinutes !== undefined && lastLocationUpdateMinutes <= MAX_OFFLINE_MINUTES;

  const maxDriverNameLength = 33;
  const formattedDriverName = driverName.length > maxDriverNameLength ? `${driverName.slice(0, maxDriverNameLength - 1)}...` : driverName;
  return (
    <PopoverContentDiv $isLive={isLive} data-testid="driver-location-content">
      <p className="driver-name">{formattedDriverName}</p>
      <div className="bottom-content">
        <LocationStatusLabel isLive={isLive} />
        <span className="location-update-timestamp">
          {getTimeStampString(lastLocationUpdateMinutes, t)}
        </span>
      </div>
    </PopoverContentDiv>
  );
};

export default DriverLocationPopoverContent;
